/**
 * preview_update tool — show what update_flow would change, without writing.
 *
 * Runs the exact same resolution path as update_flow (merge/patch/replace
 * semantics, optional validation) and diffs the result against the live flow,
 * so the preview can't drift from what the update would actually apply.
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { FlowDefinition } from "../api/types.js";
import { resolveFlowUpdate, updateFlowInputSchema, describeUpdateInputError } from "./update-flow.js";
import { logger } from "../utils/logger.js";

export const previewUpdateInputSchema = updateFlowInputSchema;

export const previewUpdateTool = {
  name: "preview_update",
  description:
    "Preview exactly what update_flow would change on a flow — display name, trigger, added/removed/modified actions, and connection references — without writing anything. Run this before update_flow on flows that matter.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: { type: "string", description: "The flow ID (GUID) to preview changes against" },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      displayName: { type: "string", description: "Proposed new display name" },
      description: { type: "string", description: "Proposed new description" },
      trigger: { type: "object", description: "Proposed replacement trigger definition" },
      triggerName: { type: "string", description: "Name for the trigger when replacing it" },
      actions: {
        type: "object",
        description: "Proposed actions (replaces all actions unless mergeActions is true)",
      },
      mergeActions: {
        type: "boolean",
        description: "Deep-merge the supplied actions onto the current ones instead of replacing",
      },
      patchActions: {
        type: "object",
        description: "Path-based patches applied on top of the resolved actions",
      },
      connectionReferences: {
        type: "object",
        description: "Proposed connection references",
      },
      validateBeforeUpdate: {
        type: "boolean",
        description: "Run flow validation as part of the preview",
      },
    },
    required: ["flowId"],
  },
};

/** Stable stringify so key ordering never shows up as a false difference. */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, val: unknown) => {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const record = val as Record<string, unknown>;
      return Object.fromEntries(Object.keys(record).sort().map((k) => [k, record[k]]));
    }
    return val;
  });
}

function diffKeyed(
  before: Readonly<Record<string, unknown>> | undefined,
  after: Readonly<Record<string, unknown>> | undefined,
): { added: string[]; removed: string[]; modified: string[] } {
  const beforeKeys = new Set(Object.keys(before ?? {}));
  const afterKeys = new Set(Object.keys(after ?? {}));
  const added = [...afterKeys].filter((k) => !beforeKeys.has(k));
  const removed = [...beforeKeys].filter((k) => !afterKeys.has(k));
  const modified = [...afterKeys].filter(
    (k) => beforeKeys.has(k) && canonical(before?.[k]) !== canonical(after?.[k]),
  );
  return { added, removed, modified };
}

function summarizeList(label: string, items: string[]): string | undefined {
  if (items.length === 0) return undefined;
  return `  ${label}: ${items.join(", ")}`;
}

export async function handlePreviewUpdate(
  api: FlowManagementApi,
  input: unknown,
): Promise<string> {
  let parsed;
  try {
    parsed = previewUpdateInputSchema.parse(input);
  } catch (err) {
    if (err instanceof z.ZodError) {
      return `Preview blocked — invalid input:\n\n${describeUpdateInputError(err)}`;
    }
    throw err;
  }
  logger.info({ flowId: parsed.flowId }, "Executing preview_update");

  const resolved = await resolveFlowUpdate(api, parsed);
  if (!resolved.ok) {
    return `Preview blocked — the update would be rejected:\n\n${resolved.message}`;
  }

  const { currentFlow, newDefinition, newDisplayName, newConnRefs } = resolved;
  const currentDef: FlowDefinition = currentFlow.properties.definition;
  const changes: string[] = [];

  if (newDisplayName !== currentFlow.properties.displayName) {
    changes.push(`Display name: "${currentFlow.properties.displayName}" → "${newDisplayName}"`);
  }

  const currentDescription = currentFlow.properties.description ?? currentDef.description;
  if ((newDefinition.description ?? undefined) !== (currentDescription ?? undefined)) {
    changes.push(
      `Description: ${currentDescription ? `"${currentDescription}"` : "(none)"} → ${newDefinition.description ? `"${newDefinition.description}"` : "(cleared)"}`,
    );
  }

  const triggerDiff = diffKeyed(currentDef.triggers, newDefinition.triggers);
  if (triggerDiff.added.length || triggerDiff.removed.length || triggerDiff.modified.length) {
    changes.push(
      "Triggers:",
      ...[
        summarizeList("added", triggerDiff.added),
        summarizeList("removed", triggerDiff.removed),
        summarizeList("modified", triggerDiff.modified),
      ].filter((l): l is string => l !== undefined),
    );
  }

  const actionDiff = diffKeyed(currentDef.actions, newDefinition.actions);
  if (actionDiff.added.length || actionDiff.removed.length || actionDiff.modified.length) {
    changes.push(
      "Actions:",
      ...[
        summarizeList("added", actionDiff.added),
        summarizeList("removed", actionDiff.removed),
        summarizeList("modified", actionDiff.modified),
      ].filter((l): l is string => l !== undefined),
    );
  }

  const connDiff = diffKeyed(currentFlow.properties.connectionReferences, newConnRefs);
  if (connDiff.added.length || connDiff.removed.length || connDiff.modified.length) {
    changes.push(
      "Connection references:",
      ...[
        summarizeList("added", connDiff.added),
        summarizeList("removed", connDiff.removed),
        summarizeList("modified", connDiff.modified),
      ].filter((l): l is string => l !== undefined),
    );
  }

  if (changes.length === 0) {
    return `No changes: the proposed update matches "${currentFlow.properties.displayName}" (${parsed.flowId}) as it stands. Running update_flow would be a no-op.`;
  }

  return [
    `Preview for "${currentFlow.properties.displayName}" (${parsed.flowId}) — nothing has been written:`,
    "",
    ...changes,
    "",
    "Apply with update_flow using the same arguments.",
  ].join("\n");
}
